import { useState, useEffect } from 'react';
import { useProductCatalog } from '../queries/useProductCatalog';
import type { BillingPeriod, Currency, ProductId, Sku } from '../api/model';
import { ProductCatalogRepository } from './product-catalog-repository';
import type { EnhancedProduct, ProductInformation } from './product-catalog-repository.types';

interface ProductCatalogRepoResult {
  isLoading: boolean;
  getPlansByProductId: (productId: ProductId) => EnhancedProduct | undefined;
  getProductBySku: (sku: Sku) => ProductInformation | undefined;
  getBasicAllowanceSkus: () => Sku[] | undefined;
}

interface Props {
  billingPeriod: BillingPeriod;
  currency: Currency;
  enabled?: boolean;
}

export const useProductCatalogRepo = ({
  billingPeriod,
  currency,
  enabled = true,
}: Props): ProductCatalogRepoResult => {
  const [repository, setRepository] = useState<ProductCatalogRepository | undefined>();
  const { data: productCatalog, isLoading } = useProductCatalog({
    billingPeriod,
    currency,
    enabled,
  });

  useEffect(() => {
    if (productCatalog) {
      setRepository(new ProductCatalogRepository(productCatalog));
    }
  }, [productCatalog]);

  const getPlansByProductId = (productId: ProductId) => {
    if (!repository) {
      return undefined;
    }
    return repository.getPlansByProductId(productId);
  };

  const getProductBySku = (sku: Sku) => {
    if (!repository) {
      return undefined;
    }
    return repository.getProductBySku(sku);
  };

  const getBasicAllowanceSkus = () => {
    if (!repository) {
      return undefined;
    }

    return repository.getBasicAllowanceSkus();
  };

  return {
    isLoading,
    getPlansByProductId,
    getProductBySku,
    getBasicAllowanceSkus,
  };
};
